// Credits service — guest credits in localStorage, logged-in credits in Firestore.
//
// 2.3.0 부터 로그인 사용자 잔액은 10배 단위 지갑이다 (functions/credits.js):
//   wallet.dailyUsed / wallet.day → 매일 config.daily 로 리셋되는 무료 quota
//   wallet.balance                → 가입 보너스·초대 보상·팩. 리셋 안 됨.
// 가격과 지급량은 config/credits (./credit-config.js). 여기서는 표시용으로만
// 읽는다 — 실제 게이트는 서버가 건다.
//
// 게스트 잔액만 "디자인 개수" 로 localStorage 에 있다 (구버전과 같은 키·단위라
// 업데이트해도 남은 횟수가 그대로다). 화면엔 price.generate 를 곱해 크레딧으로.
import { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { auth, db } from '../firebase.js';
import { getCreditConfig, loadCreditConfig, useCreditConfig } from './credit-config.js';

const GUEST_CREDITS_KEY = 'voda_guest_credits';
// 옛 필드 → 지갑 환산 배수. 서버 credit-config.js 의 LEGACY_SCALE 과 같다.
const LEGACY_SCALE = 10;

// 사용자 로컬 자정 기준 "YYYY-MM-DD". 서버 dayKey() 와 같은 방식 (en-CA = ISO).
// 서버는 users/{uid}.timezone 을 쓰고, 그 값은 이 브라우저가 보낸 것이라 일치.
function todayKey() {
  try { return new Intl.DateTimeFormat('en-CA').format(new Date()); }
  catch { return new Date().toISOString().slice(0, 10); }
}

/**
 * 오늘 기준 지갑 — 서버 functions/credits.js 의 walletOf 와 같은 식.
 * 아직 서버가 환산하지 않은 사용자는 옛 필드 × 10 으로 보여준다 (다음 생성 때
 * 서버가 같은 값으로 옮긴다).
 */
function walletOf(data, today) {
  const w = data && data.wallet;
  if (w && w.v === 2) {
    // 날짜는 앞으로만 — 저장된 날이 더 나중이면 그날 사용량 그대로 (서버 functions/credits.js 와 같다).
    const same = w.day === today || (typeof w.day === 'string' && w.day > today);
    return { dailyUsed: same ? Math.max(0, w.dailyUsed || 0) : 0, balance: Math.max(0, w.balance || 0) };
  }
  const usedOld = data.creditDayKey === today ? (data.creditDailyUsed || 0) : 0;
  const balOld = data.creditBonus !== undefined ? (data.creditBonus || 0) : (data.credits || 0);
  return { dailyUsed: usedOld * LEGACY_SCALE, balance: balOld * LEGACY_SCALE };
}

// Custom event so React hook re-renders when localStorage changes in the same tab.
const GUEST_CREDITS_CHANGED = 'voda:guestCreditsChanged';

function readGuestCredits() {
  try {
    const raw = localStorage.getItem(GUEST_CREDITS_KEY);
    if (raw === null) {
      // First visit — issue initial credits.
      const initial = getCreditConfig().guestDesigns;
      localStorage.setItem(GUEST_CREDITS_KEY, String(initial));
      return initial;
    }
    const n = parseInt(raw, 10);
    return Number.isFinite(n) && n >= 0 ? n : 0;
  } catch {
    return getCreditConfig().guestDesigns;
  }
}

function writeGuestCredits(n) {
  try {
    localStorage.setItem(GUEST_CREDITS_KEY, String(Math.max(0, n)));
    window.dispatchEvent(new CustomEvent(GUEST_CREDITS_CHANGED));
  } catch (e) {
    console.warn('Failed to write guest credits:', e);
  }
}

export const CreditsService = {
  getGuestCredits: readGuestCredits,

  // 게스트는 "디자인 개수" 단위 — 사진 여러 장이면 그만큼 빠진다. 로그인
  // 사용자는 서버가 처리하지만 (functions/credits.js reserveCredits) 게스트 잔액은
  // 여기 localStorage 뿐이라 서버 변경이 닿지 않는다.
  decrementGuest(count = 1) {
    const n = Math.max(1, Math.floor(Number(count) || 1));
    const current = readGuestCredits();
    if (current <= 0) return 0;
    const next = Math.max(0, current - n);
    writeGuestCredits(next);
    return next;
  },

  // Peek the transferable guest credit count without clearing.
  // Caller must invoke clearGuestCredits() only after the server confirms
  // the signup transfer was applied.
  peekGuestForTransfer() {
    return Math.min(readGuestCredits(), getCreditConfig().guestDesigns);
  },

  clearGuestCredits() {
    writeGuestCredits(0);
  },
};

function guestState() {
  const n = readGuestCredits();
  const credits = n * getCreditConfig().price.generate;
  return {
    credits, dailyRemaining: credits, bonus: 0, guestDesigns: n,
    plan: 'free', proTrialEndsAt: null,
    referralCode: '', referredBy: null, inviteCount: 0,
    isGuest: true, loading: false,
  };
}

/**
 * React hook — returns the user's current credit state.
 * Guest: localStorage; logged-in: Firestore onSnapshot.
 *
 * `credits` = 지금 쓸 수 있는 총 크레딧 (dailyRemaining + bonus). 호출부는 대부분
 * 이것만 보면 된다. 잔여를 "30 중 20 + 보너스 100" 처럼 쪼개서 보여줘야 하는
 * 화면 (Account) 만 dailyRemaining / bonus 를 따로 읽는다. 가격·일일량은
 * useCreditConfig() 로.
 */
export { useCreditConfig };

export function useCredits() {
  const [state, setState] = useState(() => {
    const user = auth.currentUser;
    if (!user || user.isAnonymous) return guestState();
    return {
      credits: null, dailyRemaining: 0, bonus: 0,
      plan: 'free', proTrialEndsAt: null,
      referralCode: '', referredBy: null, inviteCount: 0,
      isGuest: false,
      loading: true, // logged-in users start loading until snapshot arrives
    };
  });

  useEffect(() => {
    let unsubDoc = null;

    const handleAuth = (user) => {
      const isGuest = !user || user.isAnonymous;
      if (unsubDoc) { unsubDoc(); unsubDoc = null; }

      if (isGuest) {
        setState(guestState());
      } else {
        setState(prev => ({ ...prev, isGuest: false, loading: true }));
        unsubDoc = onSnapshot(
          doc(db, 'users', user.uid),
          (snap) => {
            const data = snap.data();
            // 신규 가입 직후 race condition 처리: Firebase Auth 가 user 를
            // resolve 한 직후 onSnapshot 이 즉시 발사되는데, 그 시점에 server
            // initializeUser 가 아직 credit 필드를 안 쓴 상태일 수 있음.
            // 그러면 loading 유지 (false 로 떨어뜨리면 UI 가 "0 크레딧" 으로
            // 잠시 잘못 표시됨).
            //
            // 판정 기준은 creditBonus 하나가 아니라 "둘 중 아무것도 없음" —
            // 크레딧 전환 이전에 가입한 사용자는 legacy `credits` 만 갖고 있고,
            // 그들의 initializeUser 는 다음 실행 때 필드를 채운다. creditBonus
            // 만 보면 그 사이 영영 loading 에 갇힌다.
            const uninitialized = !snap.exists()
              || (!data?.wallet && data?.creditBonus === undefined && data?.credits === undefined);
            if (uninitialized) {
              setState(prev => ({ ...prev, isGuest: false, loading: true }));
              return;
            }
            // daily 는 lazy reset — 서버가 자정에 뭘 써 주는 게 아니라,
            // wallet.day 가 오늘이 아니면 "아직 0개 씀" 으로 친다.
            const w = walletOf(data, todayKey());
            const dailyRemaining = Math.max(0, getCreditConfig().daily - w.dailyUsed);
            const bonus = w.balance;
            setState({
              credits: dailyRemaining + bonus,
              dailyRemaining,
              bonus,
              plan: data.plan || 'free',
              proTrialEndsAt: data.proTrialEndsAt || null,
              referralCode: data.referralCode || '',
              referredBy: data.referredBy || null,
              inviteCount: data.inviteCount || 0,
              isGuest: false,
              loading: false,
            });
          },
          (err) => {
            console.warn('Credits subscription error:', err);
            setState(prev => ({ ...prev, credits: 0, isGuest: false, loading: false }));
          }
        );
      }
    };

    const unsubAuth = auth.onAuthStateChanged(handleAuth);
    // 일일량·가격이 기본값과 다르면 문서를 받은 뒤 다시 계산해야 한다.
    loadCreditConfig().then(() => handleAuth(auth.currentUser));

    const onGuestChange = () => {
      if (!auth.currentUser || auth.currentUser.isAnonymous) setState(guestState());
    };
    window.addEventListener(GUEST_CREDITS_CHANGED, onGuestChange);
    window.addEventListener('storage', onGuestChange); // other tabs

    return () => {
      unsubAuth();
      if (unsubDoc) unsubDoc();
      window.removeEventListener(GUEST_CREDITS_CHANGED, onGuestChange);
      window.removeEventListener('storage', onGuestChange);
    };
  }, []);

  return state;
}
